// src/pages/TrackedAssets.tsx
// Main page for qmLEO Assets Module - Tracked Assets list
// Created: 2025-01-31

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Plus, Filter, RefreshCw, AlertCircle, CheckCircle, Clock,
  AlertTriangle, Package, MapPin, MoreVertical, Eye, Edit, Trash2, ClipboardCheck
} from 'lucide-react';
import { assetsAPI } from '../services/assetsAPI';
import { 
  TrackedAsset, AssetCategory, AssetStatus, VerificationStatus, FUNDING_SOURCE_LABELS
} from '../types/asset';
import { logger } from '../utils/logger';
import AddAssetModal from '../components/assets/AddAssetModal';
import EditAssetModal from '../components/assets/EditAssetModal';
import AssetDetailModal from '../components/assets/AssetDetailModal';
import DisposeAssetModal from '../components/assets/DisposeAssetModal';
import VerifyAssetModal from '../components/assets/VerifyAssetModal';

const pageLogger = logger.createServiceLogger('TrackedAssetsPage');

const TrackedAssets: React.FC = () => {
  const [assets, setAssets] = useState<TrackedAsset[]>([]);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<AssetStatus | 'all'>('active');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [verificationFilter, setVerificationFilter] = useState<VerificationStatus | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDisposeModal, setShowDisposeModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<TrackedAsset | null>(null);
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.action-menu-container')) {
        setOpenActionMenu(null);
      }
    };
    if (openActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openActionMenu]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [assetsResponse, categoriesResponse] = await Promise.all([
        assetsAPI.getAssets({ status: statusFilter === 'all' ? undefined : statusFilter }),
        assetsAPI.getCategories()
      ]);
      if (assetsResponse.success && assetsResponse.data) {
        setAssets(assetsResponse.data.assets || []);
      } else {
        throw new Error(assetsResponse.error?.message || 'Failed to load assets');
      }
      if (categoriesResponse.success && categoriesResponse.data) {
        setCategories(categoriesResponse.data.categories || []);
      }
    } catch (err: any) {
      pageLogger.error('Error loading data', { error: err.message });
      setError(err.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    console.log('loadAssets called with statusFilter:', statusFilter);
    try {
      const response = await assetsAPI.getAssets({
        status: statusFilter,
        categoryId: categoryFilter || undefined,
        verificationStatus: verificationFilter === 'all' ? undefined : verificationFilter,
        search: searchTerm || undefined
      });
      if (response.success && response.data) {
        setAssets(response.data.assets || []);
      }
    } catch (err: any) {
      pageLogger.error('Error refreshing assets', { error: err.message });
    }
  };

  useEffect(() => {
    console.log('Filter changed - statusFilter:', statusFilter, 'loading:', loading);
    loadAssets();
  }, [statusFilter, categoryFilter, verificationFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading) loadAssets();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getVerificationBadge = (status: VerificationStatus) => {
    const badges: Record<VerificationStatus, JSX.Element> = {
      verified: (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />Verified
        </span>
      ),
      pending: (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />Due Soon
        </span>
      ),
      overdue: (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="w-3 h-3 mr-1" />Overdue
        </span>
      ),
      new: (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Package className="w-3 h-3 mr-1" />New
        </span>
      )
    };
    return badges[status] || null;
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  const handleAddAsset = async (assetData: any) => {
    try {
      const response = await assetsAPI.createAsset(assetData);
      if (response.success) {
        setShowAddModal(false);
        loadAssets();
      } else {
        throw new Error(response.error?.message || 'Failed to create asset');
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleUpdateAsset = async (assetId: string, assetData: any) => {
    try {
      const response = await assetsAPI.updateAsset(assetId, assetData);
      if (response.success) {
        setShowEditModal(false);
        setSelectedAsset(null);
        loadAssets();
      } else {
        throw new Error(response.error?.message || 'Failed to update asset');
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDisposeAsset = async (assetId: string, disposalData: any) => {
    try {
      const response = await assetsAPI.disposeAsset(assetId, disposalData);
      if (response.success) {
        setShowDisposeModal(false);
        setSelectedAsset(null);
        loadAssets();
      } else {
        throw new Error(response.error?.message || 'Failed to dispose asset');
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const openEdit = (asset: TrackedAsset) => {
    setSelectedAsset(asset);
    setShowEditModal(true);
    setOpenActionMenu(null);
  };

  const openDetail = (asset: TrackedAsset) => {
    setSelectedAsset(asset);
    setShowDetailModal(true);
    setOpenActionMenu(null);
  };

  const openDispose = (asset: TrackedAsset) => {
    setSelectedAsset(asset);
    setShowDisposeModal(true);
    setOpenActionMenu(null);
  };

  const openVerify = (asset: TrackedAsset) => {
    setSelectedAsset(asset);
    setShowVerifyModal(true);
    setOpenActionMenu(null);
  };

  const handleAssetVerified = (updatedAsset: TrackedAsset) => {
    setAssets(prev => prev.map(a => a.assetId === updatedAsset.assetId ? updatedAsset : a));
  };

  const activeCategories = categories.filter(c => c.isActive);
  const totalActive = assets.filter(a => a.status === 'active').length;
  const overdueCount = assets.filter(a => a.verificationStatus === 'overdue').length;
  const pendingCount = assets.filter(a => a.verificationStatus === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading assets...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tracked Assets</h1>
          <p className="text-sm text-gray-500 mt-1">High-value items requiring annual verification</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors"
          style={{ backgroundColor: '#082759' }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0a2e6b'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#082759'}
        >
          <Plus className="w-5 h-5 mr-2" />Add Asset
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Active</p>
              <p className="text-2xl font-bold text-gray-900">{totalActive}</p>
            </div>
            <Package className="w-10 h-10 text-blue-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Verification Overdue</p>
              <p className="text-2xl font-bold text-red-600">{overdueCount}</p>
            </div>
            <AlertTriangle className="w-10 h-10 text-red-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Due Soon</p>
              <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
            </div>
            <Clock className="w-10 h-10 text-yellow-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Categories</p>
              <p className="text-2xl font-bold text-gray-900">{activeCategories.length}</p>
            </div>
            <Filter className="w-10 h-10 text-gray-400 opacity-50" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by asset tag, name, serial number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as AssetStatus | 'all')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active Only</option>
              <option value="disposed">Disposed</option>
              <option value="all">All Status</option>
            </select>
            <select
              value={verificationFilter}
              onChange={(e) => setVerificationFilter(e.target.value as VerificationStatus | 'all')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Verification</option>
              <option value="overdue">Overdue</option>
              <option value="pending">Due Soon</option>
              <option value="verified">Verified</option>
              <option value="new">New</option>
            </select>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg border ${showFilters ? 'bg-blue-50 border-blue-300' : 'border-gray-300'}`}
            >
              <Filter className="w-5 h-5 text-gray-600" />
            </button>
            <button onClick={loadAssets} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50">
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {activeCategories.map(cat => (
                <option key={cat.categoryId} value={cat.categoryId}>{cat.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verification</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {assets.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No assets found</p>
                    <p className="text-sm mt-1">
                      {searchTerm || categoryFilter || verificationFilter !== 'all'
                        ? 'Try adjusting your filters'
                        : 'Add your first tracked asset to get started'}
                    </p>
                  </td>
                </tr>
              ) : (
                assets.map((asset) => (
                  <tr key={asset.assetId} className={`hover:bg-gray-50 ${asset.status === 'disposed' ? 'opacity-60' : ''}`}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center">
                          <span className="font-mono text-sm font-medium text-blue-600">{asset.assetTag}</span>
                          {asset.status === 'disposed' && (
                            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">Disposed</span>
                          )}
                        </div>
                        <div className="text-sm font-medium text-gray-900 mt-1">{asset.name}</div>
                        {asset.serialNumber && <div className="text-xs text-gray-500">S/N: {asset.serialNumber}</div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{asset.categoryName}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-1 text-gray-400" />{asset.location}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{formatCurrency(asset.currentValue || asset.purchaseValue)}</div>
                        {asset.fundingSource && (
                          <div className="text-xs text-gray-500">{FUNDING_SOURCE_LABELS[asset.fundingSource]}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        {getVerificationBadge(asset.verificationStatus)}
                        {asset.lastVerifiedAt && (
                          <div className="text-xs text-gray-500 mt-1">Last: {formatDate(asset.lastVerifiedAt)}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="action-menu-container relative inline-block">
                        <button
                          onClick={() => setOpenActionMenu(openActionMenu === asset.assetId ? null : asset.assetId)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-500" />
                        </button>
                        {openActionMenu === asset.assetId && (
                          <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                            <button onClick={() => openDetail(asset)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                              <Eye className="w-4 h-4 mr-2" />View Details
                            </button>
                            {asset.status === 'active' && (
                              <>
                                <button onClick={() => openVerify(asset)} className="w-full px-4 py-2 text-left text-sm text-green-700 hover:bg-green-50 flex items-center">
                                  <ClipboardCheck className="w-4 h-4 mr-2" />Verify Asset
                                </button>
                                <button onClick={() => openEdit(asset)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                                  <Edit className="w-4 h-4 mr-2" />Edit Asset
                                </button>
                                <hr className="my-1" />
                                <button onClick={() => openDispose(asset)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center">
                                  <Trash2 className="w-4 h-4 mr-2" />Dispose Asset
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddAssetModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddAsset}
        categories={activeCategories}
      />

      {selectedAsset && (
        <>
          <EditAssetModal
            isOpen={showEditModal}
            onClose={() => { setShowEditModal(false); setSelectedAsset(null); }}
            onSave={(data) => handleUpdateAsset(selectedAsset.assetId, data)}
            asset={selectedAsset}
            categories={activeCategories}
          />
          <AssetDetailModal
            isOpen={showDetailModal}
            onClose={() => { setShowDetailModal(false); setSelectedAsset(null); }}
            asset={selectedAsset}
            onEdit={() => { setShowDetailModal(false); setShowEditModal(true); }}
            onDispose={() => { setShowDetailModal(false); setShowDisposeModal(true); }}
          />
          <DisposeAssetModal
            isOpen={showDisposeModal}
            onClose={() => { setShowDisposeModal(false); setSelectedAsset(null); }}
            onConfirm={(data) => handleDisposeAsset(selectedAsset.assetId, data)}
            asset={selectedAsset}
          />
          <VerifyAssetModal
            isOpen={showVerifyModal}
            onClose={() => { setShowVerifyModal(false); setSelectedAsset(null); }}
            onVerified={handleAssetVerified}
            asset={selectedAsset}
          />
        </>
      )}
    </div>
  );
};

export default TrackedAssets;
